/**
 * Reveal.
 *
 * The moment the game is actually about. It has to be legible at a glance,
 * generous on a win and not humiliating on a miss, and it has to make the two
 * colours sit next to each other so the player can *see* what they were off
 * by — that comparison is the thing they will remember tomorrow.
 */

import { append, h, ICON, svg } from '../dom';
import { ctx } from '../ctx';
import { hexToOklab } from '../../core/colour';
import { GRADES, describeDistance, formatDuration } from '../../game/grade';
import { swatch } from '../paint';
import { burst, toast } from '../chrome';
import { countUp, haptic, reducedMotion } from '../motion';
import { nameForLab } from '../../core/palette';
import { longDate, msUntilNextPuzzle, puzzleNumber } from '../../core/schedule';
import { share } from '../share';
import * as sfx from '../audio';

/** Card and confetti colours per grade. The CSS tokens cannot be read from a canvas. */
const GRADE_HEX: Record<string, string> = {
  perfect: '#7ff2c0',
  excellent: '#a5d8ff',
  great: '#d9c9ff',
  close: '#ffd9a0',
  miss: '#ff9d9d',
};

export function revealScreen(): HTMLElement {
  const result = ctx.result;
  const puzzle = ctx.puzzle;
  const root = h('div', { class: 'screen reveal' });

  if (!result || !puzzle) {
    append(root, [
      h('div', { class: 'bar' }, h('div', { class: 'bar__spacer' })),
      h('div', { class: 'scroll' }, h('p', { class: 'prose', text: 'Nothing to reveal yet.' })),
      h('button', {
        class: 'btn btn--primary btn--wide',
        text: 'Home',
        on: { click: () => ctx.go('home') },
      }),
    ]);
    return root;
  }

  const meta = GRADES[result.grade];
  const won = result.grade !== 'miss';

  root.style.setProperty('--grade', `var(--${meta.token})`);
  root.style.setProperty('--grade-glow', `var(--${meta.token}-glow)`);

  // --- Bar -----------------------------------------------------------------
  root.appendChild(
    h(
      'div',
      { class: 'bar' },
      h(
        'button',
        {
          class: 'icon',
          attrs: { type: 'button', 'aria-label': 'Home' },
          on: { click: () => ctx.go('home') },
        },
        svg(ICON.back),
      ),
      h('div', { class: 'bar__title', text: `Puzzle #${puzzleNumber(puzzle.index)} · ${longDate(puzzle.index)}` }),
    ),
  );

  const scroll = h('div', { class: 'scroll' });

  // --- Verdict -------------------------------------------------------------
  scroll.appendChild(
    h(
      'div',
      { class: 'verdict' },
      h('div', { class: 'verdict__symbol', text: meta.symbol, attrs: { 'aria-hidden': 'true' } }),
      h('div', { class: 'verdict__word', text: meta.label }),
      h('div', { class: 'verdict__blurb', text: meta.blurb }),
    ),
  );

  // --- The two colours -----------------------------------------------------
  const yours = h(
    'div',
    { class: 'compare__half compare__half--bad', style: swatch(result.hex) },
    h('div', { class: 'compare__k', text: 'Yours' }),
    h('div', { class: 'compare__name', text: nameForLab(hexToOklab(result.hex)) }),
  );

  const theirs = h(
    'div',
    { class: 'compare__half compare__half--good', style: swatch(result.targetHex) },
    h('div', { class: 'compare__k', text: 'Target' }),
    h('div', { class: 'compare__name', text: puzzle.target.name }),
  );

  scroll.appendChild(h('div', { class: 'compare' }, yours, theirs));

  // --- Numbers -------------------------------------------------------------
  const distanceNode = h('div', { class: 'row__v', text: '0' });
  const rows = h(
    'div',
    { class: 'rows' },
    row('How close', h('div', { class: 'row__v' }, distanceNode), true),
    row('In words', h('div', { class: 'row__v', text: describeDistance(result.distance) })),
    row('Blends used', h('div', { class: 'row__v', text: `${result.blends} of ${puzzle.spec.blends}` })),
    row('Time', h('div', { class: 'row__v', text: formatDuration(result.ms) })),
    row('Streak', h('div', { class: 'row__v', text: ctx.stats.streak > 0 ? `${ctx.stats.streak} days` : '—' })),
  );
  scroll.appendChild(rows);

  scroll.appendChild(
    h('div', { class: 'next' },
      h('span', { text: 'Next puzzle in' }),
      h('span', { class: 'next__t', attrs: { 'data-countdown': 'true' } }),
    ),
  );

  scroll.appendChild(h('div', { style: { height: 'var(--s-4)' } }));
  root.appendChild(scroll);

  // --- Foot ----------------------------------------------------------------
  root.appendChild(
    h(
      'div',
      { class: 'reveal__foot' },
      h(
        'button',
        {
          class: 'btn',
          attrs: { type: 'button', 'aria-label': 'Share result' },
          on: {
            click: async (e: Event) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.disabled = true;
              const how = await share({
                puzzleNumber: puzzleNumber(puzzle.index),
                weekday: longDate(puzzle.index).split(',')[0] ?? '',
                grade: result.grade,
                blendsUsed: result.blends,
                budget: puzzle.spec.blends,
                yourHex: result.hex,
                targetHex: result.targetHex,
                targetName: puzzle.target.name,
                trail: ctx.trail,
                streak: ctx.stats.streak,
              });
              btn.disabled = false;
              toast(
                how === 'shared' ? 'Shared' : how === 'copied' ? 'Copied — spoiler free' : 'Could not share',
              );
            },
          },
        },
        svg(ICON.share),
        'Share',
      ),
      h('button', {
        class: 'btn btn--primary',
        style: { flex: '1' },
        text: ctx.mode === 'archive' ? 'Back to archive' : 'Done',
        on: { click: () => ctx.go(ctx.mode === 'archive' ? 'archive' : 'home') },
      }),
    ),
  );

  // --- Celebration ---------------------------------------------------------
  if (won) {
    sfx.sWin(result.grade as 'perfect' | 'excellent' | 'great' | 'close');
    haptic('win');
    if (result.grade === 'perfect' || result.grade === 'excellent') sfx.sStreak();

    burst({
      colours: [result.hex, result.targetHex, '#ffffff', GRADE_HEX[result.grade]],
      power: result.grade === 'perfect' ? 1 : result.grade === 'excellent' ? 0.7 : 0.4,
    });
  } else {
    sfx.sMiss();
    haptic('miss');
  }

  // Distance counts up to its real value, so the number feels measured.
  if (!reducedMotion()) {
    requestAnimationFrame(() => countUp(distanceNode, result.distance, 900, 2));
  } else {
    distanceNode.textContent = result.distance.toFixed(2);
  }

  // Countdown to the next unlock, refreshed once a second.
  const tick = () => {
    const node = root.querySelector<HTMLElement>('[data-countdown]');
    if (!node) return;
    const left = msUntilNextPuzzle();
    const hours = Math.floor(left / 3_600_000);
    const minutes = Math.floor((left % 3_600_000) / 60_000);
    const seconds = Math.floor((left % 60_000) / 1000);
    node.textContent = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    return left;
  };
  tick();
  const interval = setInterval(tick, 1000);
  // The reveal is rebuilt on navigation, so the timer is cleared on a rough
  // heuristic: it is harmless if it outlives the node by a tick.
  setTimeout(() => clearInterval(interval), 3_600_000);

  return root;
}

function row(key: string, value: HTMLElement, hero = false): HTMLElement {
  return h(
    'div',
    { class: `row${hero ? ' row--hero' : ''}` },
    h('div', { class: 'row__k', text: key }),
    value,
  );
}
